import { Link, useParams } from 'react-router-dom';
import Icon from '../components/Icons';
import { useBookings } from '../booking/BookingsContext';
import FlightTicket from '../../modules/flights/FlightTicket';
import HotelVoucher from '../../modules/hotels/HotelVoucher';
import BusTicket from '../../modules/bus/BusTicket';
import SightseeingSummary from '../../modules/sightseeing/SightseeingSummary';
import InsuranceSummary from '../../modules/insurance/InsuranceSummary';
import PackageSummary from '../../modules/packages/PackageSummary';
import TransferSummary from '../../modules/transfers/TransferSummary';
import { cancellationLines } from '../../modules/transfers/data';
import BookingVoucher from './BookingVoucher';

// Modules without a dedicated document print a BookingVoucher built from their own summary.
const vouchers = {
  sightseeing: {
    title: 'Activity Voucher',
    refLabel: 'Voucher no.',
    Summary: SightseeingSummary,
    policy: () => [
      'Free cancellation up to 24 hours before the activity start time.',
      'Cancellations within 24 hours and no-shows are non-refundable.',
      'Weather-related cancellations by the operator are fully refunded.',
    ],
    info: [
      'Show this voucher (printed or on your phone) with a valid photo ID at the meeting point.',
      'Reach the meeting point at least 15 minutes before the start time.',
    ],
  },
  insurance: {
    title: 'Insurance Policy',
    refLabel: 'Policy number',
    Summary: InsuranceSummary,
    policy: (booking) => (booking.supplierBookingId ? [
      'Policies can be cancelled online up to 24 hours before cover starts.',
      'Once cover has started, the policy cannot be cancelled.',
    ] : [
      'Policies can be cancelled for a full refund (minus ₹100) before the policy start date.',
      'Once the policy period begins, it cannot be cancelled online.',
    ]),
    info: [
      'Carry this policy document while travelling.',
      'For claims or emergencies, call the 24x7 assistance number provided by the insurer.',
    ],
  },
  packages: {
    title: 'Holiday Voucher',
    refLabel: 'Holiday reference',
    Summary: PackageSummary,
    policy: () => [
      'More than 30 days before departure: 10% cancellation charge.',
      '15–30 days before departure: 50% cancellation charge.',
      'Less than 15 days before departure: non-refundable.',
      'TCS collected on international packages is always refunded.',
    ],
    info: [
      'Carry this voucher and a valid photo ID (passport for international trips) for every traveller.',
      'Hotel check-in times and sightseeing schedules are shared by our holidays team before departure.',
    ],
  },
  transfers: {
    title: 'Cab Voucher',
    refLabel: 'Cab booking ID',
    Summary: TransferSummary,
    policy: (booking) => (booking.supplierBookingId
      ? cancellationLines(booking.summary?.cancellation || [])
      : ['Free cancellation up to 24 hours before pickup.', 'Cancellations within 24 hours of pickup and no-shows are non-refundable.']),
    info: [
      'Driver and vehicle details are sent by SMS / email before pickup.',
      'Extra stops, route changes or waiting beyond the free time are charged by the operator.',
    ],
  },
};

function voucherFor(config) {
  return function Voucher({ booking }) {
    return <BookingVoucher booking={booking} {...config} policy={config.policy(booking)} />;
  };
}

// The printable document for each module.
const documents = {
  flights: FlightTicket,
  hotels: HotelVoucher,
  bus: BusTicket,
  ...Object.fromEntries(Object.entries(vouchers).map(([module, config]) => [module, voucherFor(config)])),
};

export const hasDocument = (module) => Boolean(documents[module]);

/** Standalone e-ticket or voucher page for one booking, at /documents/:bookingId (no site header or footer). */
export default function DocumentPage() {
  const { bookingId } = useParams();
  const { getBooking } = useBookings();
  const booking = getBooking(bookingId);
  const Document = booking && documents[booking.module];

  if (!Document) {
    return (
      <div className="container section empty">
        <span className="empty-icon"><Icon name="file" size={34} /></span>
        <h2>Document not found</h2>
        <p>Open your booking from My Trips on the device you booked on to download its e-ticket or voucher.</p>
        <Link to="/my-trips" className="btn btn-primary">Go to My Trips</Link>
      </div>
    );
  }

  return <Document booking={booking} />;
}
